import { useEffect, useRef } from "react"
import { useRegisterSW } from "virtual:pwa-register/react"

import { toast, useToast } from "@/hooks/use-toast"
import { ToastAction } from "@/components/ui/toast"

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

export function PWAUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      setInterval(() => {
        void registration.update()
      }, UPDATE_CHECK_INTERVAL_MS)
    },
    onRegisterError(error) {
      console.error("[PWA] Service worker registration failed:", error)
    },
  })

  const { toasts } = useToast()
  const toastIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!needRefresh || toastIdRef.current) return

    const { id } = toast({
      title: "A new version is available",
      description: "Reload to get the latest updates.",
      duration: Infinity,
      action: (
        <ToastAction
          altText="Reload to update"
          onClick={() => void updateServiceWorker(true)}
        >
          Reload
        </ToastAction>
      ),
    })
    toastIdRef.current = id
  }, [needRefresh, updateServiceWorker])

  // If the user dismisses the toast without reloading, clear needRefresh so
  // the next hourly update check can re-prompt instead of silently skipping.
  useEffect(() => {
    const id = toastIdRef.current
    if (!id) return
    const current = toasts.find((t) => t.id === id)
    if (!current || current.open === false) {
      toastIdRef.current = null
      setNeedRefresh(false)
    }
  }, [toasts, setNeedRefresh])

  return null
}
